import { useCallback, useEffect, useState } from "react";
import RefreshButton from "../RefreshButton/RefreshButton";
import { deviceOtaApi } from "../../services/deviceOtaApi";
import { deviceStatusDimensions } from "./deviceOtaStatus";
import "./DeviceOta.css";

const TABS = [
  ["overview", "Overview"],
  ["devices", "Devices"],
  ["firmware", "Firmware & Assignments"],
  ["logs", "Logs"],
];

const EVENT_STATES = ["check", "download_started", "downloaded", "verified", "boot_pending", "healthy", "failed", "rolled_back"];

function formatDate(value) {
  if (!value) return "Not reported";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function formatBytes(value) {
  if (!Number.isFinite(Number(value))) return "Unknown";
  const bytes = Number(value);
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDuration(value) {
  if (!Number.isFinite(Number(value))) return "Not reported";
  const seconds = Number(value);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  return days ? `${days}d ${hours}h` : `${hours}h ${Math.floor((seconds % 3600) / 60)}m`;
}

function StatusBadge({ value = "unknown", label, title }) {
  const normalized = String(value).toLowerCase().replaceAll("_", "-");
  return <span className={`ota-badge ota-badge-${normalized}`} title={title}>{label || String(value).replaceAll("_", " ")}</span>;
}

function Overview({ data }) {
  if (!data) return null;
  const deploymentRate = data.deployments.assigned
    ? Math.round((data.deployments.upToDate / data.deployments.assigned) * 100)
    : 100;
  return (
    <div className="ota-overview">
      <section className={`ota-health ${data.devices.attention ? "attention" : "healthy"}`}>
        <div>
          <span className="ota-section-label">Deployment health</span>
          <strong>{data.devices.attention ? `${data.devices.attention} device${data.devices.attention === 1 ? "" : "s"} need attention` : "No reported deployment failures"}</strong>
        </div>
        <span className="ota-health-rate">{deploymentRate}% assigned devices current</span>
      </section>
      <div className="ota-stat-grid">
        <section><span>Registered devices</span><strong>{data.devices.total}</strong><small>{data.devices.enabled} enabled · {data.devices.healthy} operationally healthy</small></section>
        <section><span>Active assignments</span><strong>{data.deployments.assigned}</strong><small>{data.deployments.upToDate} on assigned release</small></section>
        <section><span>Active canaries</span><strong>{data.deployments.activeCanaries}</strong><small>Awaiting health or promotion</small></section>
        <section><span>Firmware releases</span><strong>{data.releases.total}</strong><small>{data.releases.stable} stable · {data.releases.draft} draft · {data.releases.revoked} revoked</small></section>
      </div>
    </div>
  );
}

function Devices({ devices, rebootState, onReboot, onUnassign }) {
  if (!devices.length) return <div className="placeholder">No registered OTA devices.</div>;
  return (
    <div className="ota-table-wrap">
      <table className="ota-table">
        <thead><tr><th>Device</th><th>Firmware identity</th><th>Hardware</th><th>Status</th><th>Assignment</th><th>Operations</th></tr></thead>
        <tbody>{devices.map((device) => {
          const reboot = rebootState[device.deviceId];
          const statuses = deviceStatusDimensions(device);
          return (
            <tr key={device.deviceId}>
              <td><strong>{device.deviceId}</strong><span>{device.deviceFamily || "Unknown family"}</span><span>{device.enabled ? "Enabled" : "Disabled"} · credential {device.credentialConfigured ? "configured" : "missing"}</span></td>
              <td><span>Current: {device.currentFirmwareVersion || "Not confirmed"}</span><code>Release: {device.currentReleaseId || "Not confirmed"}</code><span>Reported: {device.reportedFirmwareVersion || "Not reported"}</span><code>Reported release: {device.reportedReleaseId || "Not reported"}</code></td>
              <td><span>{device.hardwareTarget || "Unknown target"}</span><span>Revision {device.hardwareRevision || "unknown"}</span><span>{device.capabilities?.length ? device.capabilities.join(", ") : "No capabilities reported"}</span></td>
              <td className="ota-device-status"><dl>{statuses.map((status) => <div key={status.key}><dt>{status.label}</dt><dd><StatusBadge value={status.value} label={status.display} title={status.title} /></dd></div>)}</dl><span>Check-in: {formatDate(device.lastCheckAt)}</span><span>Valid sample: {formatDate(device.lastValidSeenAt)}</span><span>OTA event: {device.lastStatus || "None"} · {formatDate(device.lastStatusAt)}</span><span>Presence seen: {formatDate(device.lastSeenAt)}</span><span>Uptime: {formatDuration(device.uptimeSeconds)}</span></td>
              <td><code>{device.assignedReleaseId || "Unassigned"}</code><span>{formatDate(device.assignmentUpdatedAt)}</span></td>
              <td className="ota-actions">
                <button className="btn-card-action" disabled={!device.enabled || reboot?.state === "pending"} onClick={() => onReboot(device.deviceId)}>{reboot?.state === "pending" ? "Rebooting..." : "Reboot"}</button>
                {device.assignedReleaseId && <button className="btn-card-action" onClick={() => onUnassign(device.deviceId)}>Unassign</button>}
                {reboot && <span className={`ota-action-state ${reboot.state}`}>{reboot.message}</span>}
              </td>
            </tr>
          );
        })}</tbody>
      </table>
    </div>
  );
}

function Firmware({ releases, devices, selections, setSelections, confirmation, setConfirmation, onAssign, onPromote, onRevoke }) {
  if (!releases.length) return <div className="placeholder">No firmware releases.</div>;
  return (
    <div className="ota-release-list">
      {releases.map((release) => {
        const compatible = devices.filter((device) => device.enabled && device.deviceFamily === release.deviceFamily && device.hardwareTarget === release.hardwareTarget && Number(device.maxFirmwareSize) >= Number(release.size));
        const canary = devices.find((device) => device.deviceId === release.canaryDeviceId);
        const canPromote = canary?.lastStatus === "healthy" && canary.currentReleaseId === release.releaseId;
        return (
          <article className="item-card ota-release" key={release.releaseId}>
            <div className="ota-release-heading"><div><h3>{release.version}</h3><code>{release.releaseId}</code></div><StatusBadge value={release.state} /></div>
            <dl className="ota-release-details">
              <div><dt>Target</dt><dd>{release.deviceFamily} / {release.hardwareTarget}</dd></div>
              <div><dt>Artifact</dt><dd>{formatBytes(release.size)} · SHA-256 <code title={release.sha256}>{release.sha256?.slice(0, 12) || "Unknown"}</code></dd></div>
              <div><dt>Source</dt><dd><code>{release.sourceCommit?.slice(0, 12) || "Unknown"}</code></dd></div>
              <div><dt>Created</dt><dd>{formatDate(release.createdAt)}</dd></div>
              <div><dt>Canary</dt><dd>{release.canaryDeviceId || "None"}</dd></div>
              <div><dt>Lifecycle</dt><dd>{formatDate(release.promotedAt || release.revokedAt || release.assignedAt)}</dd></div>
            </dl>
            {release.state !== "revoked" && (
              <div className="ota-release-actions">
                {release.state === "draft" && (
                  <><select aria-label={`Canary device for ${release.releaseId}`} value={selections[release.releaseId] || ""} onChange={(event) => setSelections((current) => ({ ...current, [release.releaseId]: event.target.value }))}><option value="">Select compatible device</option>{compatible.map((device) => <option key={device.deviceId} value={device.deviceId}>{device.deviceId}</option>)}</select><button className="btn-primary btn-xs" disabled={!selections[release.releaseId]} onClick={() => onAssign(selections[release.releaseId], release.releaseId)}>Assign canary</button></>
                )}
                {release.state === "canary" && <button className="btn-primary btn-xs" disabled={!canPromote} title={canPromote ? "Promote healthy canary" : "Canary must report this release healthy before promotion"} onClick={() => setConfirmation({ type: "promote", releaseId: release.releaseId })}>Promote</button>}
                <button className="btn-card-action btn-card-danger" onClick={() => setConfirmation({ type: "revoke", releaseId: release.releaseId })}>Revoke release</button>
              </div>
            )}
            {confirmation?.releaseId === release.releaseId && (
              <div className="ota-confirm" role="alert"><span>{confirmation.type === "promote" ? "Promote this canary to stable?" : "Revoke this release? Assigned devices will no longer download it."}</span><button className="btn-secondary btn-xs" onClick={() => setConfirmation(null)}>Cancel</button><button className={confirmation.type === "promote" ? "btn-primary btn-xs" : "btn-danger btn-xs"} onClick={() => confirmation.type === "promote" ? onPromote(release.releaseId) : onRevoke(release.releaseId)}>Confirm</button></div>
            )}
          </article>
        );
      })}
    </div>
  );
}

function Logs({ events, filters, setFilters, onApply }) {
  return (
    <>
      <form className="ota-filters" onSubmit={(event) => { event.preventDefault(); onApply(); }}>
        <label>Device ID<input value={filters.deviceId} onChange={(event) => setFilters((current) => ({ ...current, deviceId: event.target.value }))} placeholder="All devices" /></label>
        <label>Release ID<input value={filters.releaseId} onChange={(event) => setFilters((current) => ({ ...current, releaseId: event.target.value }))} placeholder="All releases" /></label>
        <label>State<select value={filters.state} onChange={(event) => setFilters((current) => ({ ...current, state: event.target.value }))}><option value="">All states</option>{EVENT_STATES.map((state) => <option key={state} value={state}>{state.replaceAll("_", " ")}</option>)}</select></label>
        <button className="btn-primary" type="submit">Apply</button>
      </form>
      {!events.length ? <div className="placeholder">No OTA events match these filters.</div> : <div className="ota-table-wrap"><table className="ota-table ota-log-table"><thead><tr><th>Timestamp</th><th>State</th><th>Device</th><th>Release</th><th>Firmware</th><th>Error</th></tr></thead><tbody>{events.map((event) => <tr key={`${event.deviceId}-${event.eventId}`}><td>{formatDate(event.createdAt)}</td><td><StatusBadge value={event.state} /></td><td><code>{event.deviceId}</code></td><td><code>{event.releaseId}</code></td><td>{event.firmwareVersion || "Unknown"}</td><td>{event.errorCode || "—"}</td></tr>)}</tbody></table></div>}
    </>
  );
}

export default function DeviceOta() {
  const hash = window.location.hash.replace("#", "");
  const [tab, setTab] = useState(TABS.some(([key]) => key === hash) ? hash : "overview");
  const [overview, setOverview] = useState(null);
  const [devices, setDevices] = useState([]);
  const [releases, setReleases] = useState([]);
  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({ deviceId: "", releaseId: "", state: "" });
  const [selections, setSelections] = useState({});
  const [rebootState, setRebootState] = useState({});
  const [confirmation, setConfirmation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(async (selectedTab = tab) => {
    setLoading(true);
    setError("");
    try {
      if (selectedTab === "overview") setOverview(await deviceOtaApi.overview());
      if (selectedTab === "devices") setDevices((await deviceOtaApi.devices()).devices || []);
      if (selectedTab === "firmware") {
        const [deviceData, releaseData] = await Promise.all([deviceOtaApi.devices(), deviceOtaApi.releases()]);
        setDevices(deviceData.devices || []);
        setReleases(releaseData.releases || []);
      }
      if (selectedTab === "logs") setEvents((await deviceOtaApi.events(filters)).events || []);
    } catch (requestError) {
      console.error("Failed to load OTA data:", requestError);
      setError(requestError.message || "Failed to load OTA data");
    } finally {
      setLoading(false);
    }
  }, [filters, tab]);

  useEffect(() => { load(tab); }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const switchTab = (nextTab) => {
    setTab(nextTab);
    window.location.hash = nextTab;
    setNotice("");
  };

  const mutate = async (action, successMessage, refreshTab = tab) => {
    setError("");
    setNotice("");
    try {
      await action();
      setNotice(successMessage);
      setConfirmation(null);
      await load(refreshTab);
    } catch (requestError) {
      console.error("OTA mutation failed:", requestError);
      setError(requestError.message || "OTA operation failed");
    }
  };

  const reboot = async (deviceId) => {
    setRebootState((current) => ({ ...current, [deviceId]: { state: "pending", message: "Command pending" } }));
    try {
      const result = await deviceOtaApi.reboot(deviceId);
      const message = result.delivered
        ? `Delivered · ${result.commandId}`
        : `Published, acknowledgment pending · ${result.commandId}`;
      setRebootState((current) => ({ ...current, [deviceId]: { state: "accepted", message } }));
    } catch (requestError) {
      console.error("Device reboot failed:", requestError);
      setRebootState((current) => ({ ...current, [deviceId]: { state: "error", message: requestError.message || "Reboot failed" } }));
    }
  };

  return (
    <div className="ota-page">
      <header className="ota-header"><div><h2>Device OTA</h2><span>Firmware fleet control</span></div><RefreshButton onClick={() => load(tab)} busy={loading} title="Refresh OTA data" /></header>
      <div className="tab-bar" role="tablist" aria-label="Device OTA workflows">{TABS.map(([key, label]) => <button key={key} role="tab" aria-selected={tab === key} className={`tab ${tab === key ? "active" : ""}`} onClick={() => switchTab(key)}>{label}</button>)}</div>
      {error && <div className="ota-message error" role="alert">{error}</div>}
      {notice && <div className="ota-message success" role="status">{notice}</div>}
      {loading ? <div className="placeholder">Loading...</div> : (
        <>
          {tab === "overview" && <Overview data={overview} />}
          {tab === "devices" && <Devices devices={devices} rebootState={rebootState} onReboot={reboot} onUnassign={(deviceId) => mutate(() => deviceOtaApi.unassign(deviceId), `Assignment removed from ${deviceId}`)} />}
          {tab === "firmware" && <Firmware releases={releases} devices={devices} selections={selections} setSelections={setSelections} confirmation={confirmation} setConfirmation={setConfirmation} onAssign={(deviceId, releaseId) => mutate(() => deviceOtaApi.assign(deviceId, releaseId), `${releaseId} assigned to ${deviceId}`, "firmware")} onPromote={(releaseId) => mutate(() => deviceOtaApi.promote(releaseId), `${releaseId} promoted to stable`, "firmware")} onRevoke={(releaseId) => mutate(() => deviceOtaApi.revoke(releaseId), `${releaseId} revoked`, "firmware")} />}
          {tab === "logs" && <Logs events={events} filters={filters} setFilters={setFilters} onApply={() => load("logs")} />}
        </>
      )}
    </div>
  );
}